import { I18n } from "@/i18n/I18n";
import { Plus, Search } from "lucide-react";
import { useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";

interface AssignProcedureTemplateModalProps<T extends {id: string, title?: string, description?: string}> {
  assignedIds: string[];
  createAssignment: (procedureId: string) => Promise<unknown>;
  templates: T[];
  modelLabel: string
}

export function AssignProcedureTemplateModal<T extends {id: string, title?: string, description?: string}>({ 
  assignedIds, 
  createAssignment,
  templates,
  modelLabel
}: AssignProcedureTemplateModalProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProcedureIds, setSelectedProcedureIds] = useState<string[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const availableTemplates = templates.filter(
    template => !assignedIds.includes(template.id)
  );

  const filteredTemplates = availableTemplates.filter(template =>
    template.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleTemplateSelection = (procedureId: string) => {
    setSelectedProcedureIds(prev => 
      prev.includes(procedureId) 
        ? prev.filter(id => id !== procedureId)
        : [...prev, procedureId]
    );
  };

  const handleAssign = async () => {
    if (selectedProcedureIds.length === 0) return;

    setIsAssigning(true);
    try {
      for (const procedureId of selectedProcedureIds) {
        await createAssignment(procedureId);
      }
      setShowModal(false);
    } finally {
      setIsAssigning(false);
      setSelectedProcedureIds([]);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedProcedureIds([]);
    setSearchQuery('');
  };

  const allTemplatesAssigned = availableTemplates.length === 0;
  const assignDisabled = selectedProcedureIds.length === 0 || isAssigning;

  return (
    <>
      <button className="fusa-btn-medium-with-icon btn btn-primary" onClick={() => setShowModal(true)}>
        <Plus size={16} />
        {I18n.get('assignTemplate')}
      </button>
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="assign-template-modal"
      >
        <Modal.Body style={{ padding: 31 }}>
          <h5 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>
            {I18n.get('assignTemplates')}
          </h5>
          <p style={{ fontSize: 14, color: 'var(--neutral-700)', margin: '4px 0 0' }}>
            {I18n.get('assignTemplatesDescription', { model: modelLabel })}
          </p>

          {allTemplatesAssigned ? (
            <p className="text-center" style={{ color: 'var(--neutral-500)', padding: '40px 0' }}>
              {I18n.get('allTemplatesAssigned')}
            </p>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-end" style={{ marginTop: 20, marginBottom: 8 }}>
                <span className="list-card-title">{I18n.get('availableTemplates')}</span>
                <div className="assign-template-search form-control-neutral">
                  <Search size={20} style={{ color: 'var(--neutral-500)', flexShrink: 0 }} />
                  <input
                    className="form-control-neutral"
                    type="text"
                    placeholder={I18n.get('searchTemplates')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="template-list-container">
                {filteredTemplates.length === 0 ? (
                  <p className="text-center" style={{ color: 'var(--neutral-500)', margin: 0 }}>
                    {I18n.get('noTemplatesMatchSearch')}
                  </p>
                ) : (
                  filteredTemplates.map(template => (
                    <div
                      key={template.id}
                      className={`template-list-item ${selectedProcedureIds.includes(template.id) ? 'selected' : ''}`}
                      onClick={() => toggleTemplateSelection(template.id)}
                    >
                      <Form.Check
                        type="checkbox"
                        checked={selectedProcedureIds.includes(template.id)}
                        onChange={() => toggleTemplateSelection(template.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="template-list-item-title">{template.title}</p>
                        {template.description && (
                          <p className="template-list-item-description">{template.description}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="fusa-btn-medium btn-blue-light"
            onClick={handleClose}
          >
            {I18n.get('cancel')}
          </button>
          <button
            type="button"
            className="fusa-btn-medium fusa-btn-primary"
            onClick={() => void handleAssign()}
            disabled={assignDisabled}
          >
            {isAssigning ? (
              <>
                <Spinner size="sm" className="me-2" />
                {I18n.get('assigning')}
              </>
            ) : (
              I18n.get('assignTemplate')
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
